import React from 'react';
import { useParams } from 'react-router-dom';

import { useOrganisationService } from 'services';
import { useService } from 'hooks';
import {
  IGetOrganisationDetailsResponse,
  IBaseOrganisationRequest
} from 'services/organisationService/organisationServiceTypes';
import { IUseService } from 'hooks/useService/useService';

/**
 * Create Get Organisation Request
 *
 * @description Returns the request model required for the getOrganisationDetails service
 * @param {string} organisationId Selected organisation ID
 * @returns {IBaseOrganisationRequest} Returns the valid request model
 */
const createGetOrganisationRequest = (organisationId: string) : IBaseOrganisationRequest => {
  const request: IBaseOrganisationRequest = {
    organisationId: organisationId
  };

  return request;
};

/**
 * IOrganisationContextType
 *
 * @typedef {IOrganisationContextType}
 * @description Defines the data model for what the context returns
 * @param {IGetOrganisationDetailsResponse | undefined} organisationDetailsResponse Get organisation details response
 */
interface IOrganisationContextType {
  organisationDetailsResponse?: IUseService & IGetOrganisationDetailsResponse | undefined;
}

/**
 * OrganisationContext
 *
 * @description Organisation context
 */
export const OrganisationContext = React.createContext<IOrganisationContextType>({} as IOrganisationContextType);

/**
 * OrganisationContextProvider
 *
 * @description Provider giving child components access to the context
 * @param {string} organisationId Get organisation details response
 * @param {React.ReactNode} children Children which render inside the context
 * @returns {React.ReactNode} Returns organisation provider component
 */
export const OrganisationContextProvider: React.FC = ({ children }) => {
  const { organisationId } = useParams<{ organisationId: string; }>();
  const organisationService = useOrganisationService();
  const organisationDetailsResponse = useService<IGetOrganisationDetailsResponse>(() => organisationService.getOrganisationDetails(createGetOrganisationRequest(organisationId)));

  return (
    <OrganisationContext.Provider value={{
      organisationDetailsResponse: organisationDetailsResponse
    }}>
      {children}
    </OrganisationContext.Provider>
  );
};